import React from 'react';
import classNames from 'classnames';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'subtle';
  size?: 'small' | 'medium' | 'large';
  children?: React.ReactNode;
  leftIcon?: React.FC<{ className: string }>;
  rightIcon?: React.FC<{ className: string }>;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'small',
  children,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  className,
  disabled = false,
  type = 'button',
  ...props
}) => {
  const hasLeftIcon = !!LeftIcon;
  const hasRightIcon = !!RightIcon;
  const isIconOnly = (hasLeftIcon || hasRightIcon) && !children;

  const sizeClasses = isIconOnly
    ? classNames({
        'size-10 p-2 rounded-full': size === 'small',
        'size-12 p-4 rounded-full': size !== 'small',
      })
    : classNames(
        {
          'h-10 text-ds-text-s rounded-ds-2': size === 'small',
          'h-12 text-ds-text rounded-ds-3': size === 'medium',
          'text-ds-text rounded-ds-3': size === 'large',
        },
        {
          'py-2': size !== 'large',
          'py-[14px]': size === 'large',
        },
        {
          'pl-3': hasLeftIcon && size === 'small',
          'pl-4': hasLeftIcon && size !== 'small',
          'pl-5': !hasLeftIcon && size === 'small',
          'pl-6': !hasLeftIcon && size !== 'small',
        },
        {
          'pr-3': hasRightIcon && size === 'small',
          'pr-4': hasRightIcon && size !== 'small',
          'pr-5': !hasRightIcon && size === 'small',
          'pr-6': !hasRightIcon && size !== 'small',
        },
      );

  const variantClasses = classNames({
    // ---- PRIMARY ----
    'bg-ds-bg-surface-1 text-ds-text-inverse-1': variant === 'primary',
    'border-ds-thin border-ds-aqua-300': variant === 'primary' && !disabled,
    // hover: inset shadow fakes thicker border + brand glow (no layout shift)
    'hover:shadow-[var(--shadow-ds-button-hover)]': variant === 'primary' && !disabled,
    // pressed: brand shadow-s + dimmed text
    'active:shadow-[var(--shadow-ds-button-active)] active:text-ds-text-inverse-3':
      variant === 'primary' && !disabled,

    // ---- OUTLINE ----
    'bg-ds-bg-surface-soft-2 border border-ds-border-1 text-ds-text-1': variant === 'outline',
    // hover: inset shadow fakes 2px border + neutral shadow-m (no layout shift)
    'hover:shadow-[inset_0_0_0_1px_var(--color-ds-border-1),var(--shadow-ds-elevation-neutral-m)]':
      variant === 'outline' && !disabled,
    // pressed: neutral shadow-s + dimmed text
    'active:shadow-[inset_0_0_0_1px_var(--color-ds-border-1),var(--shadow-ds-elevation-neutral-s)] active:text-ds-text-3':
      variant === 'outline' && !disabled,

    // ---- SUBTLE ----
    'bg-ds-bg-surface-soft-3 text-ds-text-1': variant === 'subtle',
    // hover: neutral shadow-m
    'hover:shadow-ds-elevation-neutral-m': variant === 'subtle' && !disabled,
    // pressed: neutral shadow-s + dimmed text
    'active:shadow-ds-elevation-neutral-s active:text-ds-text-3': variant === 'subtle' && !disabled,

    // ---- DISABLED ----
    'opacity-40 cursor-not-allowed': disabled,
    'cursor-pointer': !disabled,
  });

  const baseClasses = classNames(
    'relative group inline-flex items-center justify-center overflow-hidden transition-all duration-400 ease-in-out',
    isIconOnly ? 'gap-0' : 'gap-2',
    'font-semibold whitespace-nowrap',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-aqua-300 focus-visible:ring-offset-2',
    sizeClasses,
    variantClasses,
    className,
  );

  const iconClasses = classNames(
    'relative z-[1] shrink-0 group-active:translate-x-0 transition-transform duration-400 ease-in-out',
    {
      'h-4 w-4': size === 'small',
      'h-5 w-5': size !== 'small',
    },
  );

  return (
    <button className={baseClasses} disabled={disabled} type={type} {...props}>
      {/* Hover overlay */}
      {!disabled && variant === 'primary' && (
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-[inherit] opacity-0 group-hover:opacity-40 group-active:opacity-0 bg-gradient-to-b from-ds-aqua-300/0 from-[23.75%] to-ds-aqua-600 transition-all duration-200 ease-in-out"
        />
      )}
      {!disabled && (variant === 'outline' || variant === 'subtle') && (
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-[inherit] bg-ds-aqua-300 opacity-0 group-hover:opacity-20 group-active:opacity-0 transition-all duration-200 ease-in-out"
        />
      )}

      {/* Pressed overlay */}
      {!disabled && (
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none rounded-[inherit] bg-ds-bg-surface-1 opacity-0 group-active:opacity-[0.05] transition-all duration-200 ease-in-out"
        />
      )}

      {LeftIcon && (
        <LeftIcon
          className={classNames(iconClasses, {
            'group-hover:-translate-x-1': !isIconOnly,
          })}
        />
      )}
      {!isIconOnly && <span className="relative z-[1]">{children}</span>}
      {RightIcon && (
        <RightIcon
          className={classNames(iconClasses, {
            'group-hover:translate-x-1': !isIconOnly,
          })}
        />
      )}
    </button>
  );
};

export default Button;
